import React, {
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  differenceWith,
  equals,
  findIndex,
  includes,
  isEmpty,
  isNil,
  lensPath,
  lensProp,
  map,
  omit,
  pipe,
  propEq,
  reject,
  set,
} from "ramda";
import { MediaContext } from "../../common/media-context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
} from "@material-ui/core";

import ColumnHeader from "./ColumnHeader";
import { useProductIdentifiers } from "../../vocabularyServices";
import { Skeleton } from "@material-ui/lab";
import {
  ProductIdentifierConstraints,
  validateProductIdentifierField,
} from "../contentValidation";

const IdentifierTypesToExclude = ["BOOK-TITLE-ID", "LEGAL-DEP-NUM"];

const getProductIdentifierInputProps = type => {
  const constraints = ProductIdentifierConstraints?.[type];
  if (isNil(constraints) || type === "DOI") return {};
  const length = constraints.length.is;
  const pattern = constraints.format.pattern;
  return {
    maxLength: length,
    pattern: pattern,
  };
};

const IdentifierSelect = ({ media, onAdd, productIdentifiersResult }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = React.useRef(null);

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  if (productIdentifiersResult.isLoading) {
    return (
      <Skeleton>
        <Button />
      </Skeleton>
    );
  }

  if (productIdentifiersResult.isSuccess) {
    return (
      <Fragment>
        <Button
          ref={anchorRef}
          color="primary"
          variant="outlined"
          onClick={() => setOpen(true)}
          aria-controls={open ? "identifier-options-menu" : undefined}
          aria-haspopup="true"
        >
          Add Identifier
        </Button>
        <Menu
          id="product-identifier-select"
          onKeyDown={handleListKeyDown}
          anchorEl={anchorRef.current}
          open={open}
          onClose={() => setOpen(false)}
          transformOrigin={{
            horizontal: "right",
            vertical: "top",
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          getContentAnchorEl={null}
        >
          {pipe(
            differenceWith(
              (option, mediaProductIdentifier) =>
                equals(option.code, mediaProductIdentifier.code),
              productIdentifiersResult.data,
            ),
            reject(option => includes(option.type, IdentifierTypesToExclude)),
            map(option => (
              <MenuItem key={option.code} onClick={() => onAdd(option)}>
                {option.label}
              </MenuItem>
            )),
          )(media.productIdentifiers)}
        </Menu>
      </Fragment>
    );
  }
};

const ProductIdentifiers = () => {
  const context = useContext(MediaContext);
  const productIdentifiers = useProductIdentifiers();

  const handleAddIdentifier = option => {
    const productIdentifier = set(lensProp("value"), "", option);
    context.setMedia(prev =>
      set(
        lensProp("productIdentifiers"),
        [productIdentifier, ...prev.productIdentifiers],
        prev,
      ),
    );
  };

  const handleRemoveIdentifier = pi => {
    context.setErrors(reject(omit([pi.code])));
    context.setMedia(prev => {
      const identifiers = reject(equals(pi), prev.productIdentifiers);
      return { ...prev, productIdentifiers: identifiers };
    });
  };

  const handleBlur = pi => {
    context.setErrors(prev =>
      validateProductIdentifierField(pi.type, pi.code, pi.value, prev),
    );
  };

  if (!context.media?.productIdentifiers) {
    return null;
  }

  return (
    <Grid container spacing={4}>
      <ColumnHeader
        header="Identifiers"
        customButton={
          <IdentifierSelect
            media={context.media}
            productIdentifiersResult={productIdentifiers}
            onAdd={handleAddIdentifier}
          />
        }
      />

      {!isEmpty(context.media.productIdentifiers) &&
        productIdentifiers.isSuccess &&
        context.media.productIdentifiers.map(pi => (
          <Grid container item xs={12} key={pi.code}>
            <TextField
              fullWidth
              value={pi.value}
              onChange={event => {
                const value = event.target.value;

                context.setMedia(prev => {
                  const identifierIndex = findIndex(
                    propEq("id", pi.id),
                    prev.productIdentifiers,
                  );

                  const productIdentifiers = set(
                    lensPath([identifierIndex, "value"]),
                    value,
                    prev.productIdentifiers,
                  );

                  return set(
                    lensPath(["productIdentifiers"]),
                    productIdentifiers,
                    prev,
                  );
                });
              }}
              error={context.validator.hasError(context.errors, pi.code)}
              helperText={context.validator.firstError(context.errors, pi.code)}
              onBlur={() => handleBlur(pi)}
              label={pi.label}
              inputProps={getProductIdentifierInputProps(pi.type)}
              InputProps={{
                endAdornment: (
                  <InputAdornment>
                    <IconButton onClick={() => handleRemoveIdentifier(pi)}>
                      <FontAwesomeIcon icon={faXmark} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        ))}
    </Grid>
  );
};

export default ProductIdentifiers;
